import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, FileText } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { ErrorAlert } from '@/components/shared/ErrorAlert';
import { Grid } from '@/components/layout/Grid';
import { DocumentCard } from '@/features/documents/components/DocumentCard';
import { DocumentUploadForm } from '@/features/documents/components/DocumentUploadForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';

export function DocumentsPage() {
  const navigate = useNavigate();
  const [showUpload, setShowUpload] = useState(false);

  const { data: documents, isLoading, error, refetch } = useQuery({
    queryKey: ['documents'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('document')
        .select('id, title, file_type, file_size, sensitivity, processing_status, created_at')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const handleUpload = async (file: File, sensitivity: 'normal' | 'confidential') => {
    const session = (await supabase.auth.getSession()).data.session;
    if (!session) return;

    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/document-upload`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sensitivity', sensitivity);

    await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.access_token}` },
      body: formData,
    });

    setShowUpload(false);
    refetch();
  };

  return (
    <div>
      <PageHeader
        title="Documents"
        action={
          <Button onClick={() => setShowUpload(true)}>
            <Plus className="mr-2 h-4 w-4" /> Upload
          </Button>
        }
      />

      {error && <ErrorAlert message={String(error)} onRetry={() => refetch()} />}

      {isLoading && (
        <Grid cols={4}>
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-40" />)}
        </Grid>
      )}

      {!isLoading && documents?.length === 0 && (
        <EmptyState
          icon={FileText}
          title="No documents uploaded"
          description="Drag and drop files or click Upload."
          actionLabel="Upload Document"
          onAction={() => setShowUpload(true)}
        />
      )}

      {documents && documents.length > 0 && (
        <Grid cols={4}>
          {documents.map((doc) => (
            <DocumentCard key={doc.id} document={doc} onClick={() => navigate(`/documents/${doc.id}`)} />
          ))}
        </Grid>
      )}

      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
          <DocumentUploadForm onUpload={handleUpload} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
